import { useEffect, useState } from "react";
import { CalendarPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const ELIGIBLE_ROLES = new Set(["teacher", "admin"]);

export const TimetableOnboardingPrompt = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preparedByName, setPreparedByName] = useState("");

  useEffect(() => {
    if (!user || !ELIGIBLE_ROLES.has(user.role)) return;
    let active = true;
    Promise.all([api.get("/me/preferences"), api.get("/timetable/onboarding-status")])
      .then(([preferences, timetable]) => {
        if (
          active
          && !preferences.data.timetable_prompt_seen
          && (timetable.data.prepared_by_name || !timetable.data.has_lessons)
          && !document.querySelector('[role="dialog"][data-state="open"]')
        ) {
          setPreparedByName(timetable.data.prepared_by_name || "");
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  const markSeen = async () => {
    setSaving(true);
    try {
      await api.put("/me/preferences", { timetable_prompt_seen: true });
      setOpen(false);
      return true;
    } catch (error) {
      toast.error(errMsg(error));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openTimetable = async () => {
    if (await markSeen()) navigate("/timetable");
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && markSeen()}>
      <DialogContent className="max-w-md" data-testid="timetable-onboarding-prompt">
        <DialogHeader>
          <DialogTitle>
            {preparedByName ? "Your timetable is ready" : "Add your timetable"}
          </DialogTitle>
          <DialogDescription>
            {preparedByName
              ? `Admin ${preparedByName} has built your timetable. Please check it is correct.`
              : "Add lessons so your practical bookings align with your teaching day."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2">
          <Button
            disabled={saving}
            onClick={markSeen}
            type="button"
            variant="outline"
            data-testid="timetable-prompt-dismiss-button"
          >
            Not now
          </Button>
          <Button
            disabled={saving}
            onClick={openTimetable}
            data-testid="timetable-prompt-open-button"
          >
            <CalendarPlus className="mr-2 h-4 w-4" />
            Open timetable
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};